import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const API_BASE =
  import.meta.env.VITE_API_GATEWAY_URL || "http://localhost:8000";

export default function DistanceAnalytics() {
  const [date, setDate] = useState("");
  const [data, setData] = useState<any>(null);
  const [monthlyData, setMonthlyData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"daily" | "monthly" | "range">("daily");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rangeData, setRangeData] = useState<any>(null);

  const fetchData = async () => {
    setLoading(true);

    try {
      let url = "";

      if (mode === "monthly") {
        const formattedDate = new Date(date).toISOString().split("T")[0];
        url = `${API_BASE}/api/war-room/distance-analytics/monthly?date=${formattedDate}`;
      } else if (mode === "daily") {
        const formattedDate = new Date(date).toISOString().split("T")[0];
        url = `${API_BASE}/api/war-room/distance-analytics?date=${formattedDate}`;
      } else {
        if (!fromDate || !toDate) {
          setLoading(false);
          return;
        }

        const formattedFrom = new Date(fromDate).toISOString().split("T")[0];
        const formattedTo = new Date(toDate).toISOString().split("T")[0];
        url = `${API_BASE}/api/war-room/distance-analytics/range?from=${formattedFrom}&to=${formattedTo}`;
      }

      const res = await fetch(url);
      const json = await res.json();

      if (mode === "monthly") {
        setMonthlyData(json);
      } else if (mode === "daily") {
        setData(json);
      } else {
        setRangeData(json);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);
  }, []);

  useEffect(() => {
    if (mode === "range") return;

    if (date) {
      fetchData();
    }
  }, [date, mode]);

  const activeData =
    mode === "monthly" ? monthlyData : mode === "range" ? rangeData : data;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const activeVehicles = activeData?.vehicles || [];
  const formatVehicleId = (vehicleId: string) =>
    vehicleId?.split(":").pop() || vehicleId;
  const showTrendAndLeaderboard = mode !== "range" && activeVehicles.length > 0;

  const [page, setPage] = useState(1);

const PAGE_SIZE = 50;

useEffect(() => {
  setPage(1);
}, [activeVehicles]);

const paginatedVehicles = useMemo(() => {
  return activeVehicles.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );
}, [activeVehicles, page]);

const topChartVehicles = useMemo(() => {
  return [...activeVehicles]
    .sort((a, b) => b.distance_km - a.distance_km)
    .slice(0, 50);
}, [activeVehicles]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: { xs: '1.45rem', md: '2.125rem' }, lineHeight: 1.15 }}
      >
        📊 Distance Analytics Dashboard
      </Typography>

      <Paper
        sx={{
          p: { xs: 1.25, md: 2 },
          mb: 2,
          display: "flex",
          gap: { xs: 1, md: 2 },
          alignItems: { xs: "stretch", sm: "center" },
          flexWrap: "wrap",
          '& .MuiTextField-root': { flex: { xs: '1 1 100%', sm: '0 0 auto' } },
          '& .MuiButton-root': { flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' }, minWidth: { xs: 0, sm: 88 } },
        }}
      >
        {mode !== "range" ? (
          <TextField
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        ) : (
          <>
            <TextField
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <TextField
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </>
        )}

        <Button
          variant={mode === "daily" ? "contained" : "outlined"}
          onClick={() => setMode("daily")}
        >
          Daily
        </Button>
        <Button
          variant={mode === "monthly" ? "contained" : "outlined"}
          onClick={() => setMode("monthly")}
        >
          Monthly
        </Button>
        <Button
          variant={mode === "range" ? "contained" : "outlined"}
          onClick={() => setMode("range")}
        >
          Custom Range
        </Button>

        <Button variant="contained" onClick={fetchData}>
          Fetch
        </Button>
      </Paper>

      {loading && <CircularProgress />}

      {activeData && (
        <>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                🚗 Total Distance
                <Typography variant="h5">
                  {Number(activeData.totalDistance || 0).toFixed(2)} km
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                🌍 CO₂ Saved
                <Typography variant="h5">
                  {Number(activeData.totalCO2 || 0).toFixed(2)} kg
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                🌳 Trees Planted Equivalent
                <Typography variant="h5">
                  {Number(activeData.totalTrees || 0).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {showTrendAndLeaderboard && (
            <>
              <Paper sx={{ p: { xs: 1.25, md: 2 }, mb: 2, overflowX: 'auto' }}>
                <Typography variant="h6">
                  {mode === "monthly"
                    ? "📊 Monthly Distance Trend"
                    : "📊 Distance Trend"}
                </Typography>

                <Box sx={{ minWidth: { xs: 640, md: 0 }, height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={topChartVehicles}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="vehicle_id"
                      interval={0}
                      height={isMobile ? 0 : 80}
                      angle={isMobile ? 0 : -45}
                      textAnchor={isMobile ? 'middle' : 'end'}
                      tick={isMobile ? false : { fontSize: 12 }}
                      tickFormatter={(value) => {
                        const shortId = formatVehicleId(String(value));
                        return shortId.length > 12
                          ? `${shortId.slice(0, 6)}...${shortId.slice(-4)}`
                          : shortId;
                      }}
                      minTickGap={10}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(value) => formatVehicleId(String(value))}
                    />
                    <Line
                      type="monotone"
                      dataKey="distance_km"
                      stroke="#1976d2"
                    />
                  </LineChart>
                </ResponsiveContainer>
                </Box>
              </Paper>

              <Paper sx={{ p: { xs: 1.25, md: 2 }, mb: 2, overflowX: 'auto' }}>
                <Typography variant="h6">
                  {mode === "monthly"
                    ? "📈 Monthly Overutilised Vehicles"
                    : "📈 Overutilised Vehicles"}
                </Typography>

                <table
                  width="100%"
                  style={{ borderCollapse: "collapse", marginTop: "12px", minWidth: "520px" }}
                >
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "10px 12px" }}>
                        Rank
                      </th>
                      <th style={{ textAlign: "left", padding: "10px 12px" }}>
                        Vehicle
                      </th>
                      <th style={{ textAlign: "right", padding: "10px 12px" }}>
                        Distance
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {[...activeVehicles]
                      .sort((a, b) => b.distance_km - a.distance_km)
                      .slice(0, 5)
                      .map((v, i) => (
                        <tr key={v.vehicle_id}>
                          <td
                            style={{ textAlign: "left", padding: "10px 12px" }}
                          >
                            {i + 1}
                          </td>
                          <td
                            style={{ textAlign: "left", padding: "10px 12px" }}
                          >
                            {formatVehicleId(v.vehicle_id)}
                          </td>
                          <td
                            style={{ textAlign: "right", padding: "10px 12px" }}
                          >
                            {Number(v.distance_km || 0).toFixed(2)} km
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Paper>
            </>
          )}
        </>
      )}

      {activeVehicles.length > 0 && (
  <>
        <Paper
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: "100%", minWidth: "640px", borderCollapse: "collapse" }}>
            <thead style={{ background: "primary.", color: "" }}>
              <tr>
                <th style={{ textAlign: "left", padding: "12px" }}>
                  🚗 Vehicle
                </th>
                <th style={{ textAlign: "right", padding: "12px" }}>
                  📏 Distance
                </th>
                <th style={{ textAlign: "right", padding: "12px" }}>
                  🌿 CO₂
                </th>
                <th style={{ textAlign: "right", padding: "12px" }}>
                  🌳 Trees
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedVehicles.map((v: any, i: number) => (
                <tr
                  key={v.vehicle_id}
                  style={{
                    background: i % 2 === 0 ? "" : "",
                    transition: "0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      i % 2 === 0 ? "" : "")
                  }
                >
                  <td style={{ padding: "10px", fontWeight: 500 }}>
                    {formatVehicleId(v.vehicle_id)}
                  </td>

                  <td
                    style={{
                      textAlign: "right",
                      padding: "10px",
                      minWidth: "180px",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 5,
                          background: "#e0e0e0",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.min(
                              (Number(v.distance_km || 0) / 200) * 100,
                              100
                            )}%`,
                            height: "100%",
                            background: "#1976d2",
                          }}
                        />
                      </Box>

                      <span style={{ minWidth: "52px", textAlign: "right" }}>
                        {Number(v.distance_km || 0).toFixed(2)}
                      </span>
                    </Box>
                  </td>

                  <td style={{ textAlign: "right", padding: "10px" }}>
                    {Number(v.co2_saved || 0).toFixed(2)}
                  </td>

                  <td style={{ textAlign: "right", padding: "10px" }}>
                    {Number(v.trees || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </Box>
      </Paper>

<Box
  display="flex"
  justifyContent="center"
  gap={2}
  mt={2}
>
  <Button
    disabled={page === 1}
    onClick={() => setPage((p) => p - 1)}
  >
    Previous
  </Button>

  <Typography>
    Page {page}
  </Typography>

  <Button
    disabled={page * PAGE_SIZE >= activeVehicles.length}
    onClick={() => setPage((p) => p + 1)}
  >
    Next
  </Button>
</Box>

  </>
)}
</Box>
  );
}
